import React, { useEffect, useRef, useState } from 'react';
import { WORKFLOW_CHROME_HEIGHT_CSS_VAR } from '../../utils/workflowBuilderLayout';
import {
  AlertCircle,
  ArrowLeft,
  ChevronDown,
  ChevronUp,
  Download,
  FileClock,
  FileUp,
  Link2,
  Loader2,
  Pause,
  Play,
  RefreshCw,
  Save,
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import WorkflowHistoryPanel from './WorkflowHistoryPanel';
import WorkflowUndoRedoButton from './WorkflowUndoRedoButton';

const VARIANT_STYLES = {
  platform: {
    shell: 'bg-zinc-900/80 border-zinc-700 text-white',
    rowBorder: 'border-zinc-700',
    back: 'text-zinc-400 hover:text-white',
    divider: 'bg-zinc-700',
    nameInput: 'text-white',
    tabActive: 'border-emerald-500 text-emerald-400',
    tabInactive: 'border-transparent text-zinc-400 hover:text-white',
    builderType: 'text-zinc-400 border-zinc-600 bg-zinc-800/50',
    testButton: 'rounded-lg bg-emerald-500/15 px-3 py-1.5 text-sm font-medium text-emerald-400 hover:bg-emerald-500/25',
    toggleTrack: 'bg-slate-600',
    toggleLabelActive: 'text-white',
    toggleLabelInactive: 'text-zinc-500',
    unsaved: 'text-orange-400',
    statusDraft: 'bg-zinc-600/50 text-zinc-400',
    statusActive: 'bg-emerald-500/20 text-emerald-400',
    statusPaused: 'bg-orange-500/20 text-orange-400',
  },
  agency: {
    shell: 'bg-white border-slate-200 text-slate-900',
    rowBorder: 'border-slate-200',
    back: 'text-slate-600 hover:text-slate-900',
    divider: 'bg-slate-200',
    nameInput: 'text-slate-900',
    tabActive: 'border-emerald-600 text-emerald-600',
    tabInactive: 'border-transparent text-slate-500 hover:text-slate-700',
    builderType: 'text-slate-600 border-slate-200 bg-slate-50',
    testButton: 'rounded-lg bg-emerald-50 px-3 py-1.5 text-sm font-medium text-emerald-700 hover:bg-emerald-100',
    toggleTrack: 'bg-slate-300',
    toggleLabelActive: 'text-slate-900',
    toggleLabelInactive: 'text-slate-400',
    unsaved: 'text-orange-600',
    statusDraft: 'bg-slate-100 text-slate-600',
    statusActive: 'bg-emerald-100 text-emerald-700',
    statusPaused: 'bg-orange-100 text-orange-700',
  },
};

const WorkflowBuilderChrome = ({
  variant = 'platform',
  backLabel = 'Back',
  onBack,
  workflowName,
  onWorkflowNameChange,
  onWorkflowNameFocus,
  onWorkflowNameBlur,
  workflowStatus = 'draft',
  hasUnsavedChanges = false,
  saving = false,
  onSave,
  onPause,
  onActivate,
  onDownload,
  isDownloading = false,
  onImportFromFile,
  onImportFromUrl,
  activateLabel = 'Activate',
  activateDisabled = false,
  showPause = false,
  showActivate = false,
  tabs = [],
  activeTab,
  onTabChange,
  undoRedo,
  onOpenRunTest,
  isPublished = false,
  onTogglePublish,
  publishDisabled = false,
  publishErrorBanner = null,
  dataTestId,
}) => {
  const styles = VARIANT_STYLES[variant] || VARIANT_STYLES.platform;
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyTab, setHistoryTab] = useState('undo');
  const [workflowJsonMenuOpen, setWorkflowJsonMenuOpen] = useState(false);
  const historyButtonRef = useRef(null);
  const topbarRef = useRef(null);
  const showWorkflowJsonMenu = (
    typeof onDownload === 'function'
    || typeof onImportFromFile === 'function'
    || typeof onImportFromUrl === 'function'
  );

  const handleDownloadJson = async () => {
    if (isDownloading || typeof onDownload !== 'function') return;
    const result = await onDownload();
    if (result === true) {
      setWorkflowJsonMenuOpen(false);
    }
  };

  const handleImportFromFile = () => {
    setWorkflowJsonMenuOpen(false);
    onImportFromFile?.();
  };

  const handleImportFromUrl = () => {
    setWorkflowJsonMenuOpen(false);
    onImportFromUrl?.();
  };

  useEffect(() => {
    const node = topbarRef.current;
    if (!node || typeof document === 'undefined') return undefined;

    const syncChromeHeight = () => {
      const height = Math.round(node.getBoundingClientRect().height);
      document.documentElement.style.setProperty(WORKFLOW_CHROME_HEIGHT_CSS_VAR, `${height}px`);
    };

    syncChromeHeight();

    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', syncChromeHeight);
      return () => window.removeEventListener('resize', syncChromeHeight);
    }

    const observer = new ResizeObserver(syncChromeHeight);
    observer.observe(node);
    return () => observer.disconnect();
  }, [publishErrorBanner, activeTab]);

  const statusBadgeClass = workflowStatus === 'draft'
    ? styles.statusDraft
    : workflowStatus === 'active'
      ? styles.statusActive
      : styles.statusPaused;

  const handleTabClick = (tabId) => {
    if (tabId === activeTab) return;
    onTabChange?.(tabId);
  };

  return (
    <div
      ref={topbarRef}
      className={cn('sticky top-0 z-[60] border-b', styles.shell, styles.rowBorder)}
      data-workflow-toolbar
      data-workflow-topbar
      data-workflow-editor-ignore-pan
      data-testid={dataTestId || 'workflow-builder-chrome'}
    >
      {/* Row 1: navigation + save */}
      <div className="flex h-14 items-center justify-between px-4">
        <div className="flex min-w-0 items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={onBack}
            className={cn('gap-2 shrink-0', styles.back)}
          >
            <ArrowLeft size={16} />
            {backLabel}
          </Button>
          <div className={cn('h-6 w-px shrink-0', styles.divider)} />
          <div className="min-w-0">
            <Input
              value={workflowName}
              onFocus={onWorkflowNameFocus}
              onBlur={onWorkflowNameBlur}
              onChange={(e) => onWorkflowNameChange?.(e.target.value)}
              className={cn(
                'border-0 bg-transparent text-lg font-semibold p-0 h-auto focus-visible:ring-0',
                styles.nameInput,
              )}
              placeholder="Workflow Name"
              data-workflow-editor-ignore-pan
            />
          </div>
          <Badge variant="outline" className={cn('text-xs border-0 shrink-0', statusBadgeClass)}>
            {workflowStatus.charAt(0).toUpperCase() + workflowStatus.slice(1)}
          </Badge>
          {hasUnsavedChanges && (
            <span className={cn('text-xs flex items-center gap-1 shrink-0', styles.unsaved)}>
              <AlertCircle size={12} />
              Unsaved changes
            </span>
          )}
        </div>

        <div className="flex items-center gap-2 shrink-0">
          <div className="relative flex items-center gap-1">
            <button
              ref={historyButtonRef}
              type="button"
              onClick={() => setHistoryOpen((open) => !open)}
              className="flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-600 hover:bg-slate-50"
              data-testid="workflow-history-button"
              aria-label="Recent changes"
              data-workflow-editor-ignore-pan
            >
              <FileClock size={16} />
            </button>

            <WorkflowUndoRedoButton
              type="undo"
              disabled={!undoRedo?.canUndo}
              onClick={undoRedo?.undo}
              shortcut={undoRedo?.shortcutLabels?.undo}
              testId="workflow-undo-button"
            />
            <WorkflowUndoRedoButton
              type="redo"
              disabled={!undoRedo?.canRedo}
              onClick={undoRedo?.redo}
              shortcut={undoRedo?.shortcutLabels?.redo}
              testId="workflow-redo-button"
            />

            <WorkflowHistoryPanel
              isOpen={historyOpen}
              onClose={() => setHistoryOpen(false)}
              past={undoRedo?.past || []}
              future={undoRedo?.future || []}
              activeTab={historyTab}
              onTabChange={setHistoryTab}
              anchorRef={historyButtonRef}
            />
          </div>

          {showWorkflowJsonMenu ? (
            <DropdownMenu open={workflowJsonMenuOpen} onOpenChange={setWorkflowJsonMenuOpen}>
              <DropdownMenuTrigger asChild>
                <Button
                  type="button"
                  variant="outline"
                  className="h-10 gap-3 rounded-xl bg-white px-5 text-slate-700 hover:bg-slate-50"
                  data-testid="workflow-json-menu-trigger"
                  data-workflow-editor-ignore-pan
                  aria-label="Workflow JSON"
                >
                  <Download className="h-5 w-5" />
                  <span className="font-semibold">Workflow JSON</span>
                  {workflowJsonMenuOpen ? (
                    <ChevronUp className="h-4 w-4" />
                  ) : (
                    <ChevronDown className="h-4 w-4" />
                  )}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="end"
                sideOffset={12}
                className="w-[450px] max-w-[calc(100vw-24px)] rounded-2xl border border-slate-700 bg-[#23242a] p-5 text-white shadow-2xl"
                data-testid="workflow-json-menu-content"
                data-workflow-editor-ignore-pan
                onCloseAutoFocus={(event) => event.preventDefault()}
              >
                <div
                  className="px-3 pb-3 text-sm font-semibold uppercase tracking-[0.18em] text-slate-400"
                  data-testid="workflow-json-menu-label"
                >
                  Workflow Portability
                </div>

                {typeof onDownload === 'function' ? (
                  <button
                    type="button"
                    onClick={handleDownloadJson}
                    disabled={isDownloading}
                    className="flex w-full items-start gap-4 rounded-xl px-3 py-4 text-left hover:bg-white/5 disabled:opacity-60"
                    data-testid="workflow-json-download"
                  >
                    <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-xl bg-white/10">
                      {isDownloading ? (
                        <Loader2 className="h-6 w-6 animate-spin" />
                      ) : (
                        <Download className="h-6 w-6" />
                      )}
                    </div>
                    <div>
                      <div className="text-xl font-semibold text-white">
                        {isDownloading ? 'Downloading...' : 'Download JSON'}
                      </div>
                      <div className="mt-1 text-base leading-6 text-slate-400">
                        Export this workflow as a portable FundElite file.
                      </div>
                    </div>
                  </button>
                ) : null}

                {typeof onDownload === 'function'
                  && (typeof onImportFromFile === 'function' || typeof onImportFromUrl === 'function') ? (
                  <DropdownMenuSeparator className="my-2 bg-white/10" />
                ) : null}

                {typeof onImportFromFile === 'function' ? (
                  <button
                    type="button"
                    onClick={handleImportFromFile}
                    className="flex w-full items-start gap-4 rounded-xl px-3 py-4 text-left hover:bg-white/5"
                    data-testid="workflow-json-import-file"
                  >
                    <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-xl bg-white/10">
                      <FileUp className="h-6 w-6" />
                    </div>
                    <div>
                      <div className="text-xl font-semibold text-white">Import from file</div>
                      <div className="mt-1 text-base leading-6 text-slate-400">
                        Upload a .json or .fundelite.json workflow.
                      </div>
                    </div>
                  </button>
                ) : null}

                {typeof onImportFromFile === 'function' && typeof onImportFromUrl === 'function' ? (
                  <DropdownMenuSeparator className="my-2 bg-white/10" />
                ) : null}

                {typeof onImportFromUrl === 'function' ? (
                  <button
                    type="button"
                    onClick={handleImportFromUrl}
                    className="flex w-full items-start gap-4 rounded-xl px-3 py-4 text-left hover:bg-white/5"
                    data-testid="workflow-json-import-url"
                  >
                    <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-xl bg-white/10">
                      <Link2 className="h-6 w-6" />
                    </div>
                    <div>
                      <div className="text-xl font-semibold text-white">Import from URL</div>
                      <div className="mt-1 text-base leading-6 text-slate-400">
                        Load a workflow from a public HTTPS address.
                      </div>
                    </div>
                  </button>
                ) : null}
              </DropdownMenuContent>
            </DropdownMenu>
          ) : null}

          <Button
            size="sm"
            className="relative gap-2 bg-emerald-600 hover:bg-emerald-700 text-white"
            onClick={onSave}
            disabled={saving}
            data-testid="workflow-save-button"
            data-workflow-editor-ignore-pan
          >
            {saving ? <RefreshCw size={16} className="animate-spin" /> : <Save size={16} />}
            Save
            {hasUnsavedChanges && !saving && (
              <span
                className="absolute -right-0.5 -top-0.5 h-2 w-2 rounded-full bg-orange-500 ring-2 ring-white"
                data-testid="workflow-unsaved-dot"
                aria-hidden="true"
              />
            )}
          </Button>

          {showPause && (
            <Button
              size="sm"
              className="gap-2 bg-orange-500 hover:bg-orange-600 text-white"
              onClick={onPause}
              data-testid="workflow-pause-button"
              data-workflow-editor-ignore-pan
            >
              <Pause size={16} />
              Pause
            </Button>
          )}

          {showActivate && (
            <Button
              size="sm"
              className="gap-2 bg-emerald-600 hover:bg-emerald-700 text-white"
              onClick={onActivate}
              disabled={activateDisabled}
              data-testid="workflow-activate-button"
              data-workflow-editor-ignore-pan
            >
              <Play size={16} />
              {activateLabel}
            </Button>
          )}
        </div>
      </div>

      {publishErrorBanner}

      {/* Row 2: builder type, tabs, test + publish */}
      <div className={cn('flex h-11 items-center gap-4 border-t px-4', styles.rowBorder)}>
        <div
          className={cn(
            'flex shrink-0 items-center gap-1.5 rounded-lg border px-2.5 py-1 text-xs font-medium',
            styles.builderType,
          )}
          data-testid="workflow-builder-type"
        >
          Standard builder
          <ChevronDown size={14} className="opacity-60" />
        </div>

        <div className="flex min-w-0 flex-1 justify-center gap-1">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => handleTabClick(tab.id)}
              className={cn(
                'flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 transition-colors',
                activeTab === tab.id ? styles.tabActive : styles.tabInactive,
              )}
              data-testid={`builder-tab-${tab.id}`}
              data-workflow-editor-ignore-pan
            >
              {tab.icon ? <tab.icon size={16} /> : null}
              {tab.label}
            </button>
          ))}
        </div>

        {activeTab === 'builder' ? (
          <div className="flex shrink-0 items-center gap-3">
            <button
              type="button"
              onClick={onOpenRunTest}
              className={styles.testButton}
              data-testid="workflow-test-workflow-button"
              data-workflow-editor-ignore-pan
            >
              Test workflow
            </button>

            <div className="flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-2 py-1">
              <span className={cn(
                'text-xs font-medium',
                !isPublished ? styles.toggleLabelActive : styles.toggleLabelInactive,
              )}
              >
                Draft
              </span>
              <button
                type="button"
                role="switch"
                aria-checked={isPublished}
                disabled={publishDisabled}
                onClick={() => onTogglePublish?.(!isPublished)}
                className={cn(
                  'relative h-6 w-11 rounded-full transition-colors',
                  isPublished ? 'bg-emerald-600' : styles.toggleTrack,
                  publishDisabled && 'opacity-50 cursor-not-allowed',
                )}
                data-testid="workflow-draft-publish-toggle"
                data-workflow-editor-ignore-pan
              >
                <span
                  className={cn(
                    'absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform',
                    isPublished ? 'translate-x-5' : 'translate-x-0.5',
                  )}
                />
              </button>
              <span className={cn(
                'text-xs font-medium',
                isPublished ? styles.toggleLabelActive : styles.toggleLabelInactive,
              )}
              >
                Publish
              </span>
            </div>
          </div>
        ) : (
          <div className="w-[220px] shrink-0" aria-hidden="true" />
        )}
      </div>
    </div>
  );
};

export default WorkflowBuilderChrome;
